import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import {
  Home, Plus, ArrowLeft, ChevronRight, Upload, FileText, Send,
  Loader2, Search, MessageSquare, BarChart3, Paperclip, X,
  CheckCircle, AlertTriangle, HelpCircle, Clock, DollarSign, Building2,
  FileDown, Download, Sparkles, ClipboardList, Reply
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api/mortgage-equalizer';

const STATUS_OPTIONS = [
  "Performing", "Payment Dispute", "Servicing Dispute", "Default Alleged",
  "Acceleration Notice", "Foreclosure Threatened", "Foreclosure Filed",
  "Sale Scheduled", "Sale Completed"
];

const DOC_CATEGORIES = [
  "Promissory Note", "Mortgage", "Deed of Trust", "Closing Disclosure",
  "Settlement Statement", "Loan Modification", "Assignment", "Allonge",
  "Substitution of Trustee", "Payment History", "Monthly Statement",
  "Escrow Statement", "Default Letter", "Acceleration Notice",
  "Foreclosure Notice", "Court Document", "Servicing Transfer Notice",
  "Prior Correspondence", "Other"
];

const DOC_OPTIONS = [
  { key: 'demand_accounting', label: 'Step 1: Demand for Full Accounting', desc: 'Request a complete breakdown of the claimed mortgage account from origination to present.' },
  { key: 'conditional_acceptance', label: 'Step 2: Conditional Acceptance for Verification', desc: 'Good-faith conditional acceptance contingent upon verification of authority, accounting, and chain of title.' },
  { key: 'affidavit_facts', label: 'Step 3: Affidavit of Status & Statement of Facts', desc: 'Sworn statement of facts based on your mortgage case, for court filing or administrative record.' },
  { key: 'opportunity_to_cure', label: 'Step 4: Opportunity to Cure', desc: 'Give the opposing party a final chance to provide the requested verification before further action.' },
  { key: 'notice_default_nonresponse', label: 'Step 5: Notice of Default & Certificate of Non-Response', desc: 'Formally certify the opposing party failed to respond and is now in default of your administrative process.' },
];

export default function MortgageEqualizer() {
  const { user } = useAuth();
  const { toast } = useToast();
  const token = localStorage.getItem('token') || localStorage.getItem('access_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [cases, setCases] = useState([]);
  const [activeCase, setActiveCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);

  // Intake form
  const [intake, setIntake] = useState({
    property: { address: '', purchase_date: '', purchase_price: '', loan_amount: '', current_balance: '', primary_residence: 'Yes' },
    original_transaction: { lender: '', broker: '', servicer: '', closing_date: '', note_date: '', interest_rate: '', payment_terms: '' },
    current_claimant: { servicer: '', creditor: '', trustee: '', law_firm: '', other_entities: '' },
    mortgage_status: 'Performing',
  });

  // Documents
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('Other');

  // Analysis
  const [analyzing, setAnalyzing] = useState(false);

  // Chat
  const [chatHistory, setChatHistory] = useState([]);
  const [chatMsg, setChatMsg] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSession, setChatSession] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [fileUploading, setFileUploading] = useState(false);
  const chatFileRef = useRef(null);
  const chatEndRef = useRef(null);

  // Document generation
  const [generatingDoc, setGeneratingDoc] = useState(false);
  const [generatedDoc, setGeneratedDoc] = useState(null);
  const [selectedDocType, setSelectedDocType] = useState('demand_accounting');
  const [savedGenDocs, setSavedGenDocs] = useState([]);

  // Response analyzer
  const [responseText, setResponseText] = useState('');
  const [analyzingResponse, setAnalyzingResponse] = useState(false);
  const [responseAnalysis, setResponseAnalysis] = useState(null);
  const [responseFileUploading, setResponseFileUploading] = useState(false);
  const responseFileRef = useRef(null);

  useEffect(() => { loadCases(); }, []);

  const loadCases = async () => {
    try {
      const res = await axios.get(`${API}/cases`, { headers });
      setCases(res.data);
    } catch { }
    setLoading(false);
  };

  const openCase = async (caseId) => {
    try {
      const res = await axios.get(`${API}/cases/${caseId}`, { headers });
      setActiveCase(res.data);
      setChatHistory(res.data.chat_history || []);
      setCurrentStep(1);
      // Load saved generated documents
      try {
        const gdRes = await axios.get(`${API}/cases/${caseId}/generated-documents`, { headers });
        setSavedGenDocs(gdRes.data || []);
      } catch { setSavedGenDocs([]); }
    } catch { toast({ title: 'Error loading case', variant: 'destructive' }); }
  };

  const createCase = async () => {
    try {
      const res = await axios.post(`${API}/cases`, intake, { headers });
      if (res.data.success) {
        setActiveCase(res.data.case);
        setChatHistory([]);
        loadCases();
        toast({ title: 'Case Created', description: 'Your mortgage case has been created.' });
        setCurrentStep(2);
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to create case', variant: 'destructive' });
    }
  };

  const updateField = (section, field, value) => {
    setIntake(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  };

  // ─── Document Upload ───
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeCase) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', uploadCategory);
      await axios.post(`${API}/cases/${activeCase.id}/upload?category=${encodeURIComponent(uploadCategory)}`, formData, { headers });
      toast({ title: 'Document Uploaded', description: file.name });
      openCase(activeCase.id);
    } catch { toast({ title: 'Upload Failed', variant: 'destructive' }); }
    setUploading(false);
    e.target.value = '';
  };

  // ─── AI Analysis ───
  const runAnalysis = async () => {
    if (!activeCase) return;
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/cases/${activeCase.id}/analyze`, {}, { headers });
      if (res.data.job_id) {
        const jobId = res.data.job_id;
        const poll = async (attempts = 0) => {
          if (attempts >= 60) { setAnalyzing(false); toast({ title: 'Timeout', variant: 'destructive' }); return; }
          try {
            const jr = await axios.get(`${API}/jobs/${jobId}`, { headers });
            if (jr.data.status === 'completed') {
              setAnalyzing(false);
              openCase(activeCase.id);
              toast({ title: 'Analysis Complete' });
            } else if (jr.data.status === 'failed') {
              setAnalyzing(false);
              toast({ title: 'Analysis Failed', variant: 'destructive' });
            } else { setTimeout(() => poll(attempts + 1), 3000); }
          } catch { setTimeout(() => poll(attempts + 1), 3000); }
        };
        poll();
      }
    } catch { setAnalyzing(false); toast({ title: 'Error', variant: 'destructive' }); }
  };

  // ─── AI Chat ───
  const handleChatFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/remedy-generator/extract-text`, formData, { headers });
      if (res.data.success) {
        setPendingFile({ filename: res.data.filename, text: res.data.text });
        toast({ title: 'Attached', description: `${res.data.filename} ready.` });
      }
    } catch { toast({ title: 'Upload Failed', variant: 'destructive' }); }
    setFileUploading(false);
    if (chatFileRef.current) chatFileRef.current.value = '';
  };

  const sendChat = async () => {
    let msg = chatMsg.trim();
    if ((!msg && !pendingFile) || chatLoading || !activeCase) return;
    setChatMsg('');

    let fullMessage = msg || `Analyze the attached document: ${pendingFile?.filename}`;
    const displayMsg = pendingFile ? `[Attached: ${pendingFile.filename}] ${msg || 'Analyze this document'}` : msg;
    if (pendingFile) {
      fullMessage = `${msg || 'Analyze this document in the context of my mortgage case.'}\n\n--- ATTACHED: ${pendingFile.filename} ---\n${pendingFile.text}\n--- END ---`;
    }
    setPendingFile(null);

    setChatHistory(prev => [...prev, { user: displayMsg, assistant: null }]);
    setChatLoading(true);
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

    try {
      const res = await axios.post(`${API}/cases/${activeCase.id}/chat`, { message: fullMessage, session_id: chatSession }, { headers });
      if (res.data.job_id) {
        setChatSession(res.data.session_id);
        const jobId = res.data.job_id;
        const poll = async (attempts = 0) => {
          if (attempts >= 60) { setChatLoading(false); return; }
          try {
            const jr = await axios.get(`${API}/jobs/${jobId}`, { headers });
            if (jr.data.status === 'completed') {
              setChatHistory(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { ...updated[updated.length - 1], assistant: jr.data.result };
                return updated;
              });
              setChatLoading(false);
              setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
            } else if (jr.data.status === 'failed') {
              setChatHistory(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { ...updated[updated.length - 1], assistant: 'Error: ' + (jr.data.error || 'Unknown') };
                return updated;
              });
              setChatLoading(false);
            } else { setTimeout(() => poll(attempts + 1), 3000); }
          } catch { setTimeout(() => poll(attempts + 1), 3000); }
        };
        poll();
      }
    } catch {
      setChatLoading(false);
      toast({ title: 'Chat error', variant: 'destructive' });
    }
  };

  const documents = activeCase?.documents || [];

  // ─── PHASE 2 HANDLERS ───
  const handleResponseFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResponseFileUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/remedy-generator/extract-text`, formData, { headers });
      if (res.data.success) {
        setResponseText(prev => prev + (prev ? '\n\n' : '') + res.data.text);
        toast({ title: 'Document Loaded', description: `Text from ${res.data.filename} added.` });
      }
    } catch { toast({ title: 'Upload Failed', variant: 'destructive' }); }
    setResponseFileUploading(false);
    if (responseFileRef.current) responseFileRef.current.value = '';
  };

  const analyzeResponse = async () => {
    if (!responseText.trim() || analyzingResponse || !activeCase) return;
    setAnalyzingResponse(true);
    setResponseAnalysis(null);
    try {
      const res = await axios.post(`${API}/cases/${activeCase.id}/analyze-response`, { response_text: responseText }, { headers });
      if (res.data.job_id) {
        const jobId = res.data.job_id;
        const poll = async (attempts = 0) => {
          if (attempts >= 60) { setAnalyzingResponse(false); toast({ title: 'Timeout', variant: 'destructive' }); return; }
          try {
            const jr = await axios.get(`${API}/jobs/${jobId}`, { headers });
            if (jr.data.status === 'completed') {
              setResponseAnalysis(jr.data.result);
              setAnalyzingResponse(false);
              openCase(activeCase.id);
              toast({ title: 'Analysis Complete' });
            } else if (jr.data.status === 'failed') {
              setAnalyzingResponse(false);
              toast({ title: 'Analysis Failed', variant: 'destructive' });
            } else { setTimeout(() => poll(attempts + 1), 3000); }
          } catch { setTimeout(() => poll(attempts + 1), 3000); }
        };
        poll();
      }
    } catch { setAnalyzingResponse(false); toast({ title: 'Error', variant: 'destructive' }); }
  };

  const generateDoc = async (docType) => {
    if (!activeCase || generatingDoc) return;
    setGeneratingDoc(true);
    setGeneratedDoc(null);
    setSelectedDocType(docType);
    try {
      const res = await axios.post(`${API}/cases/${activeCase.id}/generate-document`, { doc_type: docType }, { headers });
      if (res.data.job_id) {
        const jobId = res.data.job_id;
        const poll = async (attempts = 0) => {
          if (attempts >= 60) { setGeneratingDoc(false); toast({ title: 'Timeout', variant: 'destructive' }); return; }
          try {
            const jr = await axios.get(`${API}/jobs/${jobId}`, { headers });
            if (jr.data.status === 'completed') {
              setGeneratedDoc(jr.data.document);
              setGeneratingDoc(false);
              openCase(activeCase.id);
              toast({ title: 'Document Generated' });
            } else if (jr.data.status === 'failed') {
              setGeneratingDoc(false);
              toast({ title: 'Generation Failed', description: jr.data.error, variant: 'destructive' });
            } else { setTimeout(() => poll(attempts + 1), 3000); }
          } catch { setTimeout(() => poll(attempts + 1), 3000); }
        };
        poll();
      }
    } catch { setGeneratingDoc(false); toast({ title: 'Error', variant: 'destructive' }); }
  };

  const downloadGenerated = (doc) => {
    if (!doc?.content) return;
    const byteChars = atob(doc.content);
    const byteNums = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
    const blob = new Blob([new Uint8Array(byteNums)], { type: doc.mime_type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = doc.filename; a.click();
    URL.revokeObjectURL(url);
  };

  // ─── STEP TABS ───
  const steps = [
    { num: 1, label: 'Dashboard', icon: BarChart3 },
    { num: 2, label: 'Documents', icon: FileText },
    { num: 3, label: 'AI Analysis', icon: Search },
    { num: 4, label: 'Generate Docs', icon: FileDown },
    { num: 5, label: 'Response Analyzer', icon: Reply },
    { num: 6, label: 'Process Tracker', icon: ClipboardList },
    { num: 7, label: 'AI Attorney', icon: MessageSquare },
  ];

  // ─── RENDERS ───

  // Case list
  if (!activeCase) {
    return (
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6" data-testid="meq-case-list">
        <div className="flex items-center gap-3 mb-2">
          <Home className="w-7 h-7 text-amber-600" />
          <h1 className="text-2xl font-bold text-gray-900">The Mortgage Equalizer</h1>
        </div>
        <p className="text-gray-600 mb-4">AI-powered mortgage case investigator. Understand your mortgage, analyze claims, demand verification, and build a documented record grounded in Equity Jurisprudence, English Common Law, and the Magna Carta.</p>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <h3 className="font-semibold text-amber-900 text-sm mb-2">How It Works</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-amber-800">
            <div className="flex gap-2"><span className="font-bold text-amber-700">1.</span> Enter your mortgage details and create a case</div>
            <div className="flex gap-2"><span className="font-bold text-amber-700">2.</span> Upload your mortgage documents for AI analysis</div>
            <div className="flex gap-2"><span className="font-bold text-amber-700">3.</span> Run AI analysis to review authority-to-enforce and accounting</div>
            <div className="flex gap-2"><span className="font-bold text-amber-700">4.</span> Use AI Attorney Mode for guidance and document preparation</div>
          </div>
        </div>

        {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : (
          <>
            {cases.length > 0 && (
              <div className="space-y-3">
                <h2 className="font-semibold text-gray-800">Your Mortgage Cases</h2>
                {cases.map(c => (
                  <Card key={c.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openCase(c.id)} data-testid={`meq-case-${c.id}`}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{c.property?.address || 'New Case'}</p>
                        <p className="text-sm text-gray-500">{c.mortgage_status} · {c.documents?.length || 0} docs</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{c.mortgage_status}</Badge>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Intake Form */}
            <Card className="border-amber-200">
              <CardContent className="pt-6 space-y-5">
                <h2 className="font-bold text-lg text-gray-900 flex items-center gap-2"><Plus className="w-5 h-5 text-amber-600" /> New Mortgage Case</h2>
                <p className="text-sm text-gray-600">Tell us about your mortgage. Fill in what you know — you can update details later.</p>

                {/* Property */}
                <div>
                  <h3 className="font-semibold text-sm text-gray-800 mb-2">Property Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input placeholder="Property Address" value={intake.property.address} onChange={e => updateField('property', 'address', e.target.value)} data-testid="meq-prop-address" />
                    <Input type="date" placeholder="Purchase Date" value={intake.property.purchase_date} onChange={e => updateField('property', 'purchase_date', e.target.value)} />
                    <Input placeholder="Purchase Price" value={intake.property.purchase_price} onChange={e => updateField('property', 'purchase_price', e.target.value)} />
                    <Input placeholder="Original Loan Amount" value={intake.property.loan_amount} onChange={e => updateField('property', 'loan_amount', e.target.value)} />
                    <Input placeholder="Current Estimated Balance" value={intake.property.current_balance} onChange={e => updateField('property', 'current_balance', e.target.value)} />
                    <select className="border rounded-md px-3 py-2 text-sm" value={intake.property.primary_residence} onChange={e => updateField('property', 'primary_residence', e.target.value)}>
                      <option value="Yes">Primary Residence: Yes</option>
                      <option value="No">Primary Residence: No</option>
                    </select>
                  </div>
                </div>

                {/* Original Transaction */}
                <div>
                  <h3 className="font-semibold text-sm text-gray-800 mb-2">Original Transaction</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input placeholder="Original Lender" value={intake.original_transaction.lender} onChange={e => updateField('original_transaction', 'lender', e.target.value)} />
                    <Input placeholder="Mortgage Broker" value={intake.original_transaction.broker} onChange={e => updateField('original_transaction', 'broker', e.target.value)} />
                    <Input placeholder="Original Servicer" value={intake.original_transaction.servicer} onChange={e => updateField('original_transaction', 'servicer', e.target.value)} />
                    <Input type="date" placeholder="Closing Date" value={intake.original_transaction.closing_date} onChange={e => updateField('original_transaction', 'closing_date', e.target.value)} />
                    <Input placeholder="Interest Rate" value={intake.original_transaction.interest_rate} onChange={e => updateField('original_transaction', 'interest_rate', e.target.value)} />
                    <Input placeholder="Payment Terms (e.g. 30yr fixed)" value={intake.original_transaction.payment_terms} onChange={e => updateField('original_transaction', 'payment_terms', e.target.value)} />
                  </div>
                </div>

                {/* Current Claimant */}
                <div>
                  <h3 className="font-semibold text-sm text-gray-800 mb-2">Current Claimant Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input placeholder="Current Servicer" value={intake.current_claimant.servicer} onChange={e => updateField('current_claimant', 'servicer', e.target.value)} />
                    <Input placeholder="Claimed Creditor/Investor" value={intake.current_claimant.creditor} onChange={e => updateField('current_claimant', 'creditor', e.target.value)} />
                    <Input placeholder="Trustee" value={intake.current_claimant.trustee} onChange={e => updateField('current_claimant', 'trustee', e.target.value)} />
                    <Input placeholder="Foreclosure Law Firm" value={intake.current_claimant.law_firm} onChange={e => updateField('current_claimant', 'law_firm', e.target.value)} />
                  </div>
                </div>

                {/* Status */}
                <div>
                  <h3 className="font-semibold text-sm text-gray-800 mb-2">Current Mortgage Status</h3>
                  <select className="w-full border rounded-md px-3 py-2 text-sm" value={intake.mortgage_status} onChange={e => setIntake(prev => ({ ...prev, mortgage_status: e.target.value }))} data-testid="meq-status">
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <Button onClick={createCase} className="w-full bg-amber-600 hover:bg-amber-700 text-white py-5 text-base" disabled={!intake.property.address} data-testid="meq-create-case">
                  <Plus className="w-5 h-5 mr-2" /> Create Mortgage Case
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    );
  }

  // Active case view
  return (
    <div className="flex flex-col h-full" data-testid="meq-case-view">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setActiveCase(null); loadCases(); }} data-testid="meq-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 truncate">{activeCase.property?.address || 'Mortgage Case'}</p>
            <p className="text-xs text-gray-500">{activeCase.mortgage_status} · {documents.length} documents</p>
          </div>
          <Badge className="bg-amber-100 text-amber-800 text-xs">{activeCase.mortgage_status}</Badge>
        </div>
        {/* Step tabs */}
        <div className="flex gap-1 mt-3 overflow-x-auto">
          {steps.map(s => {
            const Icon = s.icon;
            return (
              <button key={s.num} onClick={() => setCurrentStep(s.num)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${currentStep === s.num ? 'border-amber-600 text-amber-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                data-testid={`meq-step-${s.num}`}>
                <Icon className="w-4 h-4" /> {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Step content */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {currentStep === 1 && renderDashboard()}
        {currentStep === 2 && renderDocuments()}
        {currentStep === 3 && renderAnalysis()}
        {currentStep === 4 && renderDocGenerator()}
        {currentStep === 5 && renderResponseAnalyzer()}
        {currentStep === 6 && renderProcessTracker()}
        {currentStep === 7 && renderChat()}
      </div>
    </div>
  );

  // ─── DASHBOARD ───
  function renderDashboard() {
    const prop = activeCase.property || {};
    const orig = activeCase.original_transaction || {};
    const claimant = activeCase.current_claimant || {};
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2"><Building2 className="w-4 h-4 text-amber-600" /><p className="text-xs font-medium text-gray-500">Property</p></div>
            <p className="font-semibold text-gray-900 text-sm">{prop.address || 'Not set'}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2"><DollarSign className="w-4 h-4 text-green-600" /><p className="text-xs font-medium text-gray-500">Claimed Balance</p></div>
            <p className="font-semibold text-gray-900 text-sm">{prop.current_balance || 'Unknown'}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-red-500" /><p className="text-xs font-medium text-gray-500">Status</p></div>
            <p className="font-semibold text-gray-900 text-sm">{activeCase.mortgage_status}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-blue-600" /><p className="text-xs font-medium text-gray-500">Documents</p></div>
            <p className="font-semibold text-gray-900 text-sm">{documents.length} uploaded</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2"><Search className="w-4 h-4 text-purple-600" /><p className="text-xs font-medium text-gray-500">AI Analysis</p></div>
            <p className="font-semibold text-gray-900 text-sm">{activeCase.analysis ? 'Completed' : 'Not run'}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-gray-500" /><p className="text-xs font-medium text-gray-500">Next Step</p></div>
            <p className="font-semibold text-gray-900 text-sm">
              {documents.length === 0 ? 'Upload documents' : !activeCase.analysis ? 'Run AI analysis' : 'Use AI Attorney'}
            </p>
          </CardContent></Card>
        </div>

        {/* Case details */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <h3 className="font-semibold text-gray-900">Case Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <div><span className="text-gray-500">Lender:</span> <span className="font-medium">{orig.lender || '—'}</span></div>
              <div><span className="text-gray-500">Servicer:</span> <span className="font-medium">{claimant.servicer || '—'}</span></div>
              <div><span className="text-gray-500">Creditor:</span> <span className="font-medium">{claimant.creditor || '—'}</span></div>
              <div><span className="text-gray-500">Trustee:</span> <span className="font-medium">{claimant.trustee || '—'}</span></div>
              <div><span className="text-gray-500">Law Firm:</span> <span className="font-medium">{claimant.law_firm || '—'}</span></div>
              <div><span className="text-gray-500">Interest Rate:</span> <span className="font-medium">{orig.interest_rate || '—'}</span></div>
              <div><span className="text-gray-500">Loan Amount:</span> <span className="font-medium">{prop.loan_amount || '—'}</span></div>
              <div><span className="text-gray-500">Purchase Price:</span> <span className="font-medium">{prop.purchase_price || '—'}</span></div>
            </div>
          </CardContent>
        </Card>

        {/* My Generated Documents */}
        {savedGenDocs.length > 0 && (
          <Card className="border-amber-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FileDown className="w-4 h-4 text-amber-600" />
                My Generated Documents
              </h3>
              <div className="space-y-2">
                {savedGenDocs.map((d, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                    <FileText className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{d.filename}</p>
                      <p className="text-xs text-gray-500">{new Date(d.generated_at).toLocaleDateString()}</p>
                    </div>
                    <Button size="sm" onClick={() => downloadGenerated({ content: d.content, filename: d.filename, mime_type: d.mime_type })} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid={`meq-redownload-${i}`}>
                      <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Audit trail */}
        {activeCase.audit_trail?.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Audit Trail</h3>
              <div className="space-y-1">
                {activeCase.audit_trail.slice(-10).reverse().map((a, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                    <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                    <span>{a.action}</span>
                    <span className="text-gray-400 ml-auto">{new Date(a.timestamp).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ─── DOCUMENTS ───
  function renderDocuments() {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <Card className="border-amber-200">
          <CardContent className="pt-5 space-y-3">
            <h3 className="font-semibold text-gray-900">Upload Mortgage Documents</h3>
            <p className="text-sm text-gray-600">Upload your promissory note, mortgage, assignments, statements, notices, and any other relevant documents.</p>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Category</label>
                <select className="w-full border rounded-md px-3 py-2 text-sm" value={uploadCategory} onChange={e => setUploadCategory(e.target.value)}>
                  {DOC_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <label className="cursor-pointer">
                <input type="file" className="hidden" onChange={handleUpload} accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg" disabled={uploading} data-testid="meq-upload-input" />
                <Button variant="outline" className="pointer-events-none" disabled={uploading}>
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </Button>
              </label>
            </div>
          </CardContent>
        </Card>

        {documents.length > 0 ? (
          <div className="space-y-2">
            {documents.map((d, i) => (
              <Card key={d.doc_id || i}>
                <CardContent className="p-3 flex items-center gap-3">
                  <FileText className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{d.filename}</p>
                    <p className="text-xs text-gray-500">{d.category}</p>
                  </div>
                  <Badge variant={d.extracted_text ? 'default' : 'outline'} className="text-xs">
                    {d.extracted_text ? 'Text Extracted' : 'No Text'}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="font-medium">No documents uploaded yet</p>
            <p className="text-sm">Upload your mortgage documents above to get started</p>
          </div>
        )}
      </div>
    );
  }

  // ─── ANALYSIS ───
  function renderAnalysis() {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h3 className="font-semibold text-purple-900">AI Mortgage Case Analysis</h3>
          <p className="text-sm text-purple-700 mt-1">The AI will review all your uploaded documents and produce a comprehensive report: Transaction Timeline, Authority-to-Enforce Review, Accounting Overview, Key Findings, and Outstanding Questions.</p>
        </div>

        {documents.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <p className="text-sm text-amber-800"><strong>Tip:</strong> Upload your mortgage documents in the Documents tab first for a more detailed analysis.</p>
            </div>
          </div>
        )}

        <Button onClick={runAnalysis} disabled={analyzing} className="w-full bg-purple-600 hover:bg-purple-700 text-white py-5" data-testid="meq-run-analysis">
          {analyzing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Search className="w-5 h-5 mr-2" />}
          {analyzing ? 'Analyzing Case...' : activeCase.analysis ? 'Re-run AI Analysis' : 'Run AI Analysis'}
        </Button>

        {analyzing && (
          <div className="text-center py-4">
            <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-purple-500" />
            <p className="text-gray-600 text-sm">AI is analyzing your documents... This may take up to 2 minutes.</p>
          </div>
        )}

        {activeCase.analysis && (
          <Card>
            <CardContent className="p-5">
              <h3 className="font-semibold text-gray-900 mb-3">Analysis Report</h3>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{activeCase.analysis}</p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }


  function renderDocGenerator() {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FileDown className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-900">Mortgage Document Generator</h3>
              <p className="text-sm text-amber-700 mt-1">Generate legal documents grounded in Equity Jurisprudence, Maxims of Equity, English Common Law, Article I Section 10 Clause 1, and the Magna Carta. All documents are customized with your case details.</p>
            </div>
          </div>
        </div>

        {/* Administrative Mailing Process */}
        <Card className="border-blue-200">
          <CardContent className="pt-5">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-blue-600" />
              The Administrative Process — 3 Mailings
            </h3>
            <div className="space-y-4 text-sm text-gray-700">
              <p>This process uses three certified mailings over a <strong>30-day administrative period</strong> to build a complete record. Here's the full process:</p>

              {/* First Mailing */}
              <div className="border border-blue-200 rounded-lg overflow-hidden">
                <div className="bg-blue-600 text-white px-4 py-2 font-semibold text-sm">First Mailing — Steps 1, 2 & 3 (Send Together) — 20-Day Response Period</div>
                <div className="p-4 space-y-2">
                  <p className="text-gray-700">Generate all three documents below and <strong>mail them together in one envelope</strong> to the mortgage servicer and lender/creditor:</p>
                  <ul className="space-y-1.5 text-gray-600 ml-1">
                    <li className="flex items-start gap-2"><span className="font-bold text-blue-700 flex-shrink-0">Step 1:</span> Demand for Full Accounting — asks for a complete breakdown of your mortgage account</li>
                    <li className="flex items-start gap-2"><span className="font-bold text-blue-700 flex-shrink-0">Step 2:</span> Conditional Acceptance — says you'll address their claim IF they can prove it's valid</li>
                    <li className="flex items-start gap-2"><span className="font-bold text-blue-700 flex-shrink-0">Step 3:</span> Affidavit of Facts — your sworn statement of the facts of your case</li>
                  </ul>
                  <p className="text-gray-600 mt-2">These three documents together demand the mortgage company prove their claim. They have <strong>20 days to respond</strong>.</p>
                </div>
              </div>

              {/* Second Mailing */}
              <div className="border border-amber-200 rounded-lg overflow-hidden">
                <div className="bg-amber-500 text-white px-4 py-2 font-semibold text-sm">Second Mailing — Step 4 (If No Response After 20 Days) — 10-Day Cure Period</div>
                <div className="p-4 space-y-2">
                  <p className="text-gray-700">If the mortgage company <strong>does not respond</strong> within 20 days of your first mailing:</p>
                  <ul className="space-y-1.5 text-gray-600 ml-1">
                    <li className="flex items-start gap-2"><span className="font-bold text-amber-700 flex-shrink-0">Step 4:</span> Opportunity to Cure — gives them one final chance (<strong>10 days</strong>) to provide the requested verification</li>
                  </ul>
                  <p className="text-gray-600 mt-2">This is a good-faith final notice. If they still don't respond within 10 days, you move to the third mailing.</p>
                </div>
              </div>

              {/* Third Mailing */}
              <div className="border border-red-200 rounded-lg overflow-hidden">
                <div className="bg-red-600 text-white px-4 py-2 font-semibold text-sm">Third Mailing — Step 5 (Day 30 — Puts Them in Default)</div>
                <div className="p-4 space-y-2">
                  <p className="text-gray-700">If they still have <strong>not responded</strong> after the 10-day Opportunity to Cure expires:</p>
                  <ul className="space-y-1.5 text-gray-600 ml-1">
                    <li className="flex items-start gap-2"><span className="font-bold text-red-700 flex-shrink-0">Step 5:</span> Notice of Default & Certificate of Non-Response — formally certifies they failed to respond and puts them in default of your administrative process</li>
                  </ul>
                  <p className="text-gray-600 mt-2">This is the final document. It certifies their silence, establishes default, and demands all adverse action cease. <strong>The entire 3-mailing process is complete in 30 days.</strong></p>
                </div>
              </div>

              {/* Timeline summary */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                <p className="font-semibold text-indigo-900 text-xs mb-1">30-Day Timeline</p>
                <div className="flex items-center gap-1 text-xs text-indigo-700">
                  <span className="bg-blue-600 text-white px-2 py-0.5 rounded font-medium">Day 1: First Mailing</span>
                  <span className="text-indigo-400">→</span>
                  <span className="text-indigo-600">20 days</span>
                  <span className="text-indigo-400">→</span>
                  <span className="bg-amber-500 text-white px-2 py-0.5 rounded font-medium">Day 20: Second Mailing</span>
                  <span className="text-indigo-400">→</span>
                  <span className="text-indigo-600">10 days</span>
                  <span className="text-indigo-400">→</span>
                  <span className="bg-red-600 text-white px-2 py-0.5 rounded font-medium">Day 30: Default</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How to Mail Instructions */}
        <Card className="border-green-200">
          <CardContent className="pt-5">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Send className="w-4 h-4 text-green-600" />
              How to Mail Your Documents — Certified Mail with Green Return Receipt
            </h3>
            <div className="space-y-3 text-sm text-gray-700">
              <p>Every document you send must go by <strong>USPS Certified Mail with a Green Return Receipt</strong>. This is how you prove — in writing — that you sent it and they received it. Follow these steps each time you mail:</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold">1</div>
                  <div>
                    <p className="font-medium text-gray-900">Print 3 Copies</p>
                    <p className="text-gray-600 text-xs mt-0.5">One for the <strong>mortgage servicer</strong>, one for the <strong>lender/creditor</strong> (if different), and one for <strong>your records</strong>. Keep your copy in a safe place.</p>
                  </div>
                </div>

                <div className="flex gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold">2</div>
                  <div>
                    <p className="font-medium text-gray-900">Go to the Post Office</p>
                    <p className="text-gray-600 text-xs mt-0.5">Tell the clerk: <strong>"I need to send this by Certified Mail with a Return Receipt."</strong> They'll know exactly what you need.</p>
                  </div>
                </div>

                <div className="flex gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold">3</div>
                  <div>
                    <p className="font-medium text-gray-900">Get These Two Items</p>
                    <p className="text-gray-600 text-xs mt-0.5">The clerk will give you a <strong>Certified Mail receipt</strong> (white slip with tracking number) and a <strong>Green Return Receipt card</strong> (PS Form 3811) — this is the green card that gets signed when they receive it.</p>
                  </div>
                </div>

                <div className="flex gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold">4</div>
                  <div>
                    <p className="font-medium text-gray-900">Fill Out the Green Card</p>
                    <p className="text-gray-600 text-xs mt-0.5">Write <strong>your name and address</strong> in the "Return To" section. Write the <strong>servicer/lender's name and address</strong> in the "Deliver To" section. Check the box marked <strong>"Certified Mail"</strong>.</p>
                  </div>
                </div>

                <div className="flex gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold">5</div>
                  <div>
                    <p className="font-medium text-gray-900">Save the White Receipt</p>
                    <p className="text-gray-600 text-xs mt-0.5">Your <strong>Certified Mail receipt</strong> has the tracking number. Keep it — you can track delivery at <strong>usps.com</strong>. This is your proof you mailed it.</p>
                  </div>
                </div>

                <div className="flex gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold">6</div>
                  <div>
                    <p className="font-medium text-gray-900">Save the Green Card When It Comes Back</p>
                    <p className="text-gray-600 text-xs mt-0.5">In about 1-2 weeks, the <strong>green card will come back to you signed</strong>. This is your legal proof they received your documents. If it comes back <strong>unclaimed</strong>, save it anyway — it still proves you tried.</p>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-1">
                <p className="font-semibold text-amber-900 text-xs flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Why This Matters</p>
                <p className="text-xs text-amber-800 mt-1">Certified Mail with a Green Return Receipt creates an <strong>official record</strong> that you sent the documents and they were received (or refused). This record is essential for the administrative process — it proves you acted in good faith and the other party had full opportunity to respond.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Document cards grouped by mailing */}
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 pt-2">
          <FileDown className="w-4 h-4 text-blue-600" />
          First Mailing — Generate These 3 Documents
        </h3>
        <div className="space-y-3">
          {DOC_OPTIONS.filter(o => ['demand_accounting','conditional_acceptance','affidavit_facts'].includes(o.key)).map(opt => (
            <Card key={opt.key} className={`border transition-colors ${selectedDocType === opt.key && generatingDoc ? 'border-amber-400 bg-amber-50' : 'hover:border-amber-300'}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 text-sm">{opt.label}</h4>
                    <p className="text-xs text-gray-600 mt-1">{opt.desc}</p>
                  </div>
                  <Button size="sm" onClick={() => generateDoc(opt.key)} disabled={generatingDoc} className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0" data-testid={`meq-gen-${opt.key}`}>
                    {generatingDoc && selectedDocType === opt.key ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                    Generate
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <h3 className="font-semibold text-gray-900 flex items-center gap-2 pt-2">
          <FileDown className="w-4 h-4 text-amber-500" />
          Second Mailing — If No Response, Give Them an Opportunity to Cure That Mistake
        </h3>
        <div className="space-y-3">
          {DOC_OPTIONS.filter(o => o.key === 'opportunity_to_cure').map(opt => (
            <Card key={opt.key} className={`border border-amber-200 transition-colors ${selectedDocType === opt.key && generatingDoc ? 'border-amber-400 bg-amber-50' : 'hover:border-amber-300'}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 text-sm">{opt.label}</h4>
                    <p className="text-xs text-gray-600 mt-1">{opt.desc}</p>
                  </div>
                  <Button size="sm" onClick={() => generateDoc(opt.key)} disabled={generatingDoc} className="bg-amber-500 hover:bg-amber-600 text-white flex-shrink-0" data-testid={`meq-gen-${opt.key}`}>
                    {generatingDoc && selectedDocType === opt.key ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                    Generate
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <h3 className="font-semibold text-gray-900 flex items-center gap-2 pt-2">
          <FileDown className="w-4 h-4 text-red-600" />
          Third Mailing — Puts Them in Default
        </h3>
        <div className="space-y-3">
          {DOC_OPTIONS.filter(o => o.key === 'notice_default_nonresponse').map(opt => (
            <Card key={opt.key} className={`border border-red-200 transition-colors ${selectedDocType === opt.key && generatingDoc ? 'border-red-400 bg-red-50' : 'hover:border-red-300'}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 text-sm">{opt.label}</h4>
                    <p className="text-xs text-gray-600 mt-1">{opt.desc}</p>
                  </div>
                  <Button size="sm" onClick={() => generateDoc(opt.key)} disabled={generatingDoc} className="bg-red-600 hover:bg-red-700 text-white flex-shrink-0" data-testid={`meq-gen-${opt.key}`}>
                    {generatingDoc && selectedDocType === opt.key ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                    Generate
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {generatingDoc && (
          <div className="text-center py-4">
            <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-amber-500" />
            <p className="text-gray-600 text-sm">AI is drafting your document... This may take up to 2 minutes.</p>
          </div>
        )}

        {generatedDoc && (
          <Card className="border-green-300 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-medium text-green-900">Document Ready</p>
                  <p className="text-sm text-green-700">{generatedDoc.filename}</p>
                </div>
                <Button onClick={() => downloadGenerated(generatedDoc)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="meq-download-doc">
                  <Download className="w-4 h-4 mr-2" /> Download DOCX
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Previously generated docs */}
        {activeCase.generated_documents?.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Previously Generated Documents</h3>
              <div className="space-y-2">
                {activeCase.generated_documents.map((d, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm p-2 bg-gray-50 rounded">
                    <FileText className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="flex-1 text-gray-800">{d.filename}</span>
                    <span className="text-xs text-gray-500">{new Date(d.generated_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  function renderResponseAnalyzer() {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Reply className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-blue-900">Response Analyzer</h3>
              <p className="text-sm text-blue-700 mt-1">Upload or paste the mortgage company's response. The AI will compare it against your outstanding requests and produce a detailed Response Matrix showing what was answered, what remains unanswered, and recommended next steps.</p>
            </div>
          </div>
        </div>

        <Card>
          <CardContent className="pt-5 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-gray-900 text-sm">Mortgage Company Response</h4>
              <label className="cursor-pointer">
                <input type="file" ref={responseFileRef} className="hidden" accept=".pdf,.doc,.docx,.txt" onChange={handleResponseFile} data-testid="meq-response-file" />
                <Button variant="outline" size="sm" className="pointer-events-none" disabled={responseFileUploading}>
                  {responseFileUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                  Upload Response
                </Button>
              </label>
            </div>
            <textarea
              value={responseText}
              onChange={e => setResponseText(e.target.value)}
              placeholder="Paste the mortgage company's response text here, or upload a document above..."
              rows={8}
              className="w-full border rounded-md px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="meq-response-text"
            />
            <Button onClick={analyzeResponse} disabled={analyzingResponse || !responseText.trim()} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4" data-testid="meq-analyze-response">
              {analyzingResponse ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Search className="w-5 h-5 mr-2" />}
              {analyzingResponse ? 'Analyzing Response...' : 'Analyze Response'}
            </Button>
          </CardContent>
        </Card>

        {analyzingResponse && (
          <div className="text-center py-4">
            <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-blue-500" />
            <p className="text-gray-600 text-sm">AI is comparing the response against your requests... This may take up to 2 minutes.</p>
          </div>
        )}

        {(responseAnalysis || activeCase.response_analysis) && (
          <Card>
            <CardContent className="p-5">
              <h3 className="font-semibold text-gray-900 mb-3">Response Analysis Report</h3>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{responseAnalysis || activeCase.response_analysis}</p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ─── PROCESS TRACKER ───
  function renderProcessTracker() {
    const trail = activeCase.audit_trail || [];
    const genDocs = activeCase.generated_documents || [];
    const hasAnalysis = !!activeCase.analysis;
    const hasResponseAnalysis = !!activeCase.response_analysis;

    const PROCESS_STEPS = [
      { label: 'Case Intake', done: true, desc: `Created ${new Date(activeCase.created_at).toLocaleDateString()}` },
      { label: 'Document Upload', done: documents.length > 0, desc: documents.length > 0 ? `${documents.length} documents uploaded` : 'Upload your mortgage documents' },
      { label: 'AI Case Analysis', done: hasAnalysis, desc: hasAnalysis ? 'Analysis completed' : 'Run AI analysis on your documents' },
      { label: 'Demand for Accounting', done: genDocs.some(d => d.doc_type === 'demand_accounting'), desc: genDocs.find(d => d.doc_type === 'demand_accounting') ? 'Generated' : 'Generate and mail this document' },
      { label: 'Conditional Acceptance', done: genDocs.some(d => d.doc_type === 'conditional_acceptance'), desc: genDocs.find(d => d.doc_type === 'conditional_acceptance') ? 'Generated' : 'Generate after mailing Demand for Accounting' },
      { label: 'Affidavit of Facts', done: genDocs.some(d => d.doc_type === 'affidavit_facts'), desc: genDocs.find(d => d.doc_type === 'affidavit_facts') ? 'Generated' : 'Generate to attach to Conditional Acceptance' },
      { label: 'Certified Mail Sent', done: trail.some(a => a.action?.toLowerCase().includes('mail')), desc: 'Send documents via USPS Certified Mail' },
      { label: 'Response Received', done: hasResponseAnalysis, desc: hasResponseAnalysis ? 'Response analyzed' : 'Upload response when received' },
      { label: 'AI Response Analysis', done: hasResponseAnalysis, desc: hasResponseAnalysis ? 'Analysis completed' : 'Analyze the mortgage company response' },
      { label: 'Opportunity to Cure', done: genDocs.some(d => d.doc_type === 'opportunity_to_cure'), desc: genDocs.find(d => d.doc_type === 'opportunity_to_cure') ? 'Generated' : 'Give them a final chance to respond' },
      { label: 'Notice of Default', done: genDocs.some(d => d.doc_type === 'notice_default_nonresponse'), desc: genDocs.find(d => d.doc_type === 'notice_default_nonresponse') ? 'Generated' : 'Certify non-response and declare default' },
    ];

    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <ClipboardList className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-indigo-900">Administrative Process Tracker</h3>
              <p className="text-sm text-indigo-700 mt-1">Track your mortgage case progress through each step of the administrative process. Each action feeds into your documented Audit Trail.</p>
            </div>
          </div>
        </div>

        {/* Visual Process Steps */}
        <Card>
          <CardContent className="p-5">
            <div className="space-y-0">
              {PROCESS_STEPS.map((step, i) => (
                <div key={i} className="flex gap-4">
                  {/* Timeline line + dot */}
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${step.done ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                      {step.done ? <CheckCircle className="w-4 h-4" /> : i + 1}
                    </div>
                    {i < PROCESS_STEPS.length - 1 && <div className={`w-0.5 h-8 ${step.done ? 'bg-green-300' : 'bg-gray-200'}`} />}
                  </div>
                  {/* Content */}
                  <div className="pb-4">
                    <p className={`font-medium text-sm ${step.done ? 'text-green-800' : 'text-gray-700'}`}>{step.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Full Audit Trail */}
        <Card>
          <CardContent className="p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Complete Audit Trail</h3>
            {trail.length > 0 ? (
              <div className="space-y-2">
                {[...trail].reverse().map((a, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm p-2 bg-gray-50 rounded">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-gray-800">{a.action}</p>
                      <p className="text-xs text-gray-400">{new Date(a.timestamp).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No audit trail entries yet.</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }


  // ─── AI ATTORNEY CHAT ───
  function renderChat() {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <MessageSquare className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-900">AI Attorney Mode</h3>
              <p className="text-sm text-amber-700 mt-1">Your dedicated mortgage case investigator. Ask questions, upload responses, get help analyzing documents and planning next steps.</p>
            </div>
          </div>
        </div>

        <Card className="flex flex-col" style={{ minHeight: '400px' }}>
          <CardContent className="flex-1 flex flex-col pt-4">
            <div className="flex-1 overflow-y-auto space-y-4 mb-4 max-h-[400px]">
              {chatHistory.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">Mortgage Case Investigator</p>
                  <p className="text-sm mt-1">Ask about your mortgage, documents, assignments, accounting, or next steps</p>
                  <div className="flex flex-wrap justify-center gap-2 mt-4">
                    {['What does my chain of title look like?', 'Who has authority to enforce my note?', 'Explain the assignments in my file', 'What documents am I missing?'].map(q => (
                      <button key={q} onClick={() => setChatMsg(q)} className="text-xs px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full border border-amber-200 hover:bg-amber-100">{q}</button>
                    ))}
                  </div>
                </div>
              ) : chatHistory.map((m, i) => (
                <div key={i} className="space-y-2">
                  <div className="flex justify-end"><div className="bg-amber-600 text-white rounded-lg px-4 py-2 max-w-[80%]"><p className="text-sm">{m.user}</p></div></div>
                  {m.assistant ? (
                    <div className="flex justify-start"><div className="bg-gray-100 text-gray-800 rounded-lg px-4 py-2 max-w-[80%]"><p className="text-sm whitespace-pre-wrap">{m.assistant}</p></div></div>
                  ) : (
                    <div className="flex justify-start"><div className="bg-gray-100 rounded-lg px-4 py-2"><Loader2 className="w-4 h-4 animate-spin text-gray-500" /></div></div>
                  )}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            {/* Pending file */}
            {pendingFile && (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg mb-2">
                <Paperclip className="w-4 h-4 text-amber-600" />
                <span className="text-sm text-amber-800 flex-1 truncate">{pendingFile.filename}</span>
                <button onClick={() => setPendingFile(null)}><X className="w-4 h-4 text-amber-400 hover:text-amber-600" /></button>
              </div>
            )}

            {/* Input */}
            <div className="flex gap-2 items-end">
              <input type="file" ref={chatFileRef} className="hidden" accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg" onChange={handleChatFile} data-testid="meq-chat-file" />
              <Button variant="outline" size="icon" onClick={() => chatFileRef.current?.click()} disabled={chatLoading || fileUploading} className="flex-shrink-0 h-10 w-10" data-testid="meq-chat-attach">
                {fileUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
              </Button>
              <Input value={chatMsg} onChange={e => setChatMsg(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') sendChat(); }} placeholder="Ask about your mortgage case..." disabled={chatLoading} data-testid="meq-chat-input" />
              <Button onClick={sendChat} disabled={chatLoading || (!chatMsg.trim() && !pendingFile)} className="bg-amber-600 hover:bg-amber-700" data-testid="meq-chat-send">
                {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}
